import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { createServerDatabase, registerServerDatabase } from "../../../actions";
import { Hint, Refusal } from "../../workspaces/WorkspaceUi";
import { NAME_PATTERN, NAME_RULE, normaliseName, suggestKey } from "./databaseForms";

// Connect a database that exists on a server - or create one where the
// server allows it - and optionally add it to a workspace straight away.
export default function AddDatabaseDialog({ open, mode, server, databaseName: preset, workspaces = [], onClose, onDone }) {
  const creating = mode === "create";
  const onServer = useMemo(() => server?.databases || [], [server]);
  // Databases that can still be connected: on the main server any of them
  // (connecting there only adds it to a workspace), elsewhere the new ones.
  const choices = useMemo(
    () => onServer.filter((d) => server?.id === "main" || !d.registered).map((d) => d.databaseName),
    [onServer, server]
  );

  const [form, setForm] = useState({ databaseName: "", name: "", label: "", workspaceId: "" });
  const [nameTouched, setNameTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const start = preset || (!creating && server?.listed && choices.length === 1 ? choices[0] : "");
    const known = onServer.find((d) => d.databaseName === start);
    setForm({ databaseName: start, name: known?.key || suggestKey(server, start), label: known?.label || "", workspaceId: "" });
    setNameTouched(Boolean(known?.registered));
    setBusy(false);
    setError("");
  }, [open, preset, creating, server, choices, onServer]);

  const databaseName = normaliseName(form.databaseName);
  const name = normaliseName(form.name);
  const known = onServer.find((d) => d.databaseName === databaseName);
  const alreadyConnected = Boolean(!creating && known?.registered);
  const databaseProblem = databaseName && !NAME_PATTERN.test(databaseName) ? `Use ${NAME_RULE}.` : "";
  const nameProblem = name && !NAME_PATTERN.test(name) ? `Use ${NAME_RULE}.` : "";
  const ready =
    databaseName && name && !databaseProblem && !nameProblem && (!alreadyConnected || form.workspaceId) && !busy;

  const setDatabase = (value) =>
    setForm((current) => {
      const next = onServer.find((d) => d.databaseName === normaliseName(value));
      return {
        ...current,
        databaseName: value,
        name: next?.registered ? next.key : nameTouched ? current.name : suggestKey(server, value),
        label: next?.registered ? next.label || "" : current.label,
      };
    });

  const submit = async () => {
    setBusy(true);
    setError("");
    const input = { databaseName, name, label: form.label.trim() || undefined, workspaceId: form.workspaceId || undefined };
    const res = creating ? await createServerDatabase(server.id, input) : await registerServerDatabase(server.id, input);
    setBusy(false);
    if (res?.success) onDone(res.msg || "Done.", res.content?.name || name);
    else setError(res?.msg || "That didn't work.");
  };

  if (!server) return null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{creating ? `Create a database on ${server.name}` : `Connect a database from ${server.name}`}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {creating
            ? "Creates an empty database with a root, so search and the explorer work straight away. Databases are never deleted from the console."
            : "The database keeps its content. Give it a name for links and, if you like, a workspace to use it."}
        </Typography>
        <Stack spacing={2}>
          {!creating && server.listed && !preset ? (
            choices.length ? (
              <TextField select label="Database on the server" value={form.databaseName} onChange={(e) => setDatabase(e.target.value)}>
                {choices.map((choice) => (
                  <MenuItem key={choice} value={choice}>
                    {choice}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Hint next="Add one to a workspace from its own page.">Every database on this server is already connected.</Hint>
            )
          ) : (
            <TextField
              label={creating ? "New database name" : "Database on the server"}
              value={form.databaseName}
              onChange={(e) => setDatabase(e.target.value)}
              disabled={Boolean(preset)}
              error={Boolean(databaseProblem)}
              helperText={databaseProblem || (creating ? `${NAME_RULE[0].toUpperCase()}${NAME_RULE.slice(1)}.` : "As it is called on the server, e.g. neo4j.")}
              autoFocus={!preset}
            />
          )}
          <TextField
            label="Name in links"
            value={form.name}
            onChange={(e) => {
              setNameTouched(true);
              setForm((current) => ({ ...current, name: e.target.value }));
            }}
            disabled={alreadyConnected}
            error={Boolean(nameProblem)}
            helperText={nameProblem || "How the database is addressed in links and the database switcher. Unique across all servers."}
          />
          <TextField
            label="Display name (optional)"
            value={form.label}
            onChange={(e) => setForm((current) => ({ ...current, label: e.target.value }))}
            disabled={alreadyConnected}
            placeholder={name || "e.g. Acme production"}
            helperText="What people see. Leave it empty to show the name above."
            inputProps={{ maxLength: 80 }}
          />
          <TextField
            select
            label="Workspace"
            value={form.workspaceId}
            onChange={(e) => setForm((current) => ({ ...current, workspaceId: e.target.value }))}
            helperText={alreadyConnected ? "Choose the workspace to add it to." : "People in the workspace can open it."}
          >
            {!alreadyConnected && <MenuItem value="">Not yet - add it to a workspace later</MenuItem>}
            {workspaces.map((workspace) => (
              <MenuItem key={workspace.id} value={workspace.id}>
                {workspace.name}
              </MenuItem>
            ))}
          </TextField>
          {error && <Refusal onClose={() => setError("")}>{error}</Refusal>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!ready} onClick={submit} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
          {busy ? (creating ? "Creating…" : "Connecting…") : creating ? "Create database" : alreadyConnected ? "Add to workspace" : "Connect database"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
